import { useState, Fragment } from 'react'
import { useRouter } from 'next/router'
import { useAuth } from 'src/hooks/useAuth'
import { Box, Typography, Menu, MenuItem, CircularProgress, Divider, Chip, IconButton, Tooltip } from '@mui/material'
import { Store as StoreIcon, KeyboardArrowDown, CheckCircle, Close } from '@mui/icons-material'

const StoreSelectorSidebar = () => {
  const router = useRouter()
  const { user, selectedChildTenant, availableChildTenants, switchTenant, clearTenantSelection } = useAuth()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [switching, setSwitching] = useState(false)

  // Only render for master tenant users
  if (!user?.isMasterTenant) {
    return null
  }

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleClearSelection = async (event: React.MouseEvent) => {
    event.stopPropagation() // Prevent dropdown from opening
    setSwitching(true)

    try {
      await clearTenantSelection()

      // Redirect to home page after clearing selection
      await router.push('/')
    } catch (error) {
      console.error('Failed to clear selection:', error)
      setSwitching(false)
    }
  }

  const handleSelectStore = async (schemaName: string) => {
    // Don't switch if already selected
    if (selectedChildTenant?.schemaName === schemaName) {
      handleClose()

      return
    }

    setSwitching(true)
    handleClose()

    try {
      await switchTenant(schemaName)

      // Emit custom event for components that need to refetch data
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('tenantChanged', { detail: { schemaName } }))
      }

      // Redirect to home page with new tenant context
      await router.push('/')
    } catch (error) {
      console.error('Failed to switch store:', error)
      setSwitching(false)
    }
  }

  const displayName = selectedChildTenant?.name || 'Select a Store'
  const isStoreSelected = !!selectedChildTenant

  return (
    <Fragment>
      <Box sx={{ px: 4, pb: 2.5 }}>
        {/* Info text */}
        <Typography
          variant='caption'
          sx={{
            color: 'text.disabled',
            fontSize: '0.6875rem',
            textTransform: 'uppercase',
            letterSpacing: '0.8px',
            fontWeight: 600,
            display: 'block',
            mb: 1.5
          }}
        >
          Current Store
        </Typography>

        {/* Store selector button */}
        <Box
          onClick={switching ? undefined : handleClick}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '12px 14px',
            borderRadius: '8px',
            cursor: switching ? 'not-allowed' : 'pointer',
            opacity: switching ? 0.7 : 1,
            transition: 'all 0.25s ease',
            backgroundColor: theme =>
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
            border: theme =>
              isStoreSelected
                ? `2px solid ${theme.palette.primary.main}`
                : `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'}`,
            '&:hover': switching
              ? {}
              : {
                  backgroundColor: theme =>
                    theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
                  borderColor: theme => theme.palette.primary.main,
                  transform: 'translateY(-1px)',
                  boxShadow: theme =>
                    theme.palette.mode === 'dark' ? '0 4px 8px rgba(0, 0, 0, 0.3)' : '0 4px 8px rgba(0, 0, 0, 0.1)'
                }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 36,
                height: 36,
                borderRadius: '6px',
                mr: 2,
                backgroundColor: theme =>
                  isStoreSelected
                    ? theme.palette.primary.main
                    : theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.08)'
              }}
            >
              <StoreIcon
                sx={{
                  fontSize: '1.25rem',
                  color: theme =>
                    isStoreSelected
                      ? theme.palette.primary.contrastText
                      : theme.palette.mode === 'dark'
                      ? 'rgba(255, 255, 255, 0.7)'
                      : 'rgba(0, 0, 0, 0.6)'
                }}
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant='body2'
                sx={{
                  fontWeight: isStoreSelected ? 600 : 500,
                  color: 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  lineHeight: 1.3
                }}
              >
                {switching ? 'Switching...' : displayName}
              </Typography>
              {isStoreSelected && selectedChildTenant && (
                <Typography
                  variant='caption'
                  sx={{
                    color: 'text.secondary',
                    fontSize: '0.7rem',
                    display: 'block',
                    mt: 0.25
                  }}
                >
                  {selectedChildTenant.schemaName}
                </Typography>
              )}
            </Box>
          </Box>

          {switching ? (
            <CircularProgress size={18} sx={{ ml: 1.5 }} />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isStoreSelected && (
                <Tooltip title='Clear selection'>
                  <IconButton
                    size='small'
                    onClick={handleClearSelection}
                    sx={{
                      padding: '4px',
                      color: 'text.secondary',
                      '&:hover': {
                        color: 'error.main',
                        backgroundColor: theme =>
                          theme.palette.mode === 'dark' ? 'rgba(244, 67, 54, 0.1)' : 'rgba(244, 67, 54, 0.08)'
                      }
                    }}
                  >
                    <Close fontSize='small' />
                  </IconButton>
                </Tooltip>
              )}
              <KeyboardArrowDown
                sx={{
                  fontSize: '1.5rem',
                  color: 'text.secondary',
                  transition: 'transform 0.25s ease',
                  transform: anchorEl ? 'rotate(180deg)' : 'rotate(0deg)'
                }}
              />
            </Box>
          )}
        </Box>

        {/* Dropdown menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          PaperProps={{
            sx: {
              minWidth: anchorEl ? anchorEl.offsetWidth : 240,
              mt: 1,
              borderRadius: '8px',
              boxShadow: theme =>
                theme.palette.mode === 'dark' ? '0 8px 24px rgba(0, 0, 0, 0.5)' : '0 8px 24px rgba(0, 0, 0, 0.15)',
              border: theme =>
                theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.12)' : '1px solid rgba(0, 0, 0, 0.08)'
            }
          }}
        >
          <Box sx={{ px: 2.5, py: 1.5 }}>
            <Typography
              variant='caption'
              sx={{
                color: 'text.secondary',
                fontSize: '0.75rem',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.5px'
              }}
            >
              Available Stores
            </Typography>
          </Box>
          <Divider sx={{ mb: 0.5 }} />

          {availableChildTenants.map(tenant => {
            const isSelected = selectedChildTenant?.id === tenant.id

            return (
              <MenuItem
                key={tenant.id}
                onClick={() => handleSelectStore(tenant.schemaName)}
                sx={{
                  py: 1.75,
                  px: 2.5,
                  mx: 1,
                  mb: 0.5,
                  borderRadius: '6px',
                  backgroundColor: isSelected
                    ? theme => (theme.palette.mode === 'dark' ? 'rgba(145, 85, 253, 0.15)' : 'rgba(145, 85, 253, 0.08)')
                    : 'transparent',
                  '&:hover': {
                    backgroundColor: theme =>
                      isSelected
                        ? theme.palette.mode === 'dark'
                          ? 'rgba(145, 85, 253, 0.2)'
                          : 'rgba(145, 85, 253, 0.12)'
                        : theme.palette.action.hover
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ flex: 1, minWidth: 0, mr: 1 }}>
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: isSelected ? 600 : 500,
                        color: 'text.primary',
                        mb: 0.25
                      }}
                    >
                      {tenant.name}
                    </Typography>
                    <Chip
                      label={tenant.schemaName}
                      size='small'
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        fontWeight: 600,
                        backgroundColor: theme =>
                          theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)',
                        color: 'text.secondary',
                        '& .MuiChip-label': {
                          px: 1
                        }
                      }}
                    />
                  </Box>
                  {isSelected && <CheckCircle fontSize='small' sx={{ color: 'primary.main', ml: 1 }} />}
                </Box>
              </MenuItem>
            )
          })}
        </Menu>
      </Box>

      {/* Warning if no store selected */}
      {!isStoreSelected && (
        <Box sx={{ px: 4, pb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              p: 2,
              borderRadius: '8px',
              backgroundColor: theme =>
                theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)',
              border: theme =>
                `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.25)'}`
            }}
          >
            <Typography
              sx={{
                fontSize: '1.125rem',
                lineHeight: 1,
                mt: 0.25
              }}
            >
              ⚠️
            </Typography>
            <Box>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: theme => (theme.palette.mode === 'dark' ? 'warning.light' : 'warning.dark'),
                  display: 'block',
                  mb: 0.5,
                  lineHeight: 1.4
                }}
              >
                No Store Selected
              </Typography>
              <Typography
                variant='caption'
                sx={{
                  fontSize: '0.75rem',
                  color: 'text.secondary',
                  display: 'block',
                  lineHeight: 1.5
                }}
              >
                Please select a store to view data
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </Fragment>
  )
}

export default StoreSelectorSidebar
